import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, AlertTriangle, Gift, ArrowRight } from 'lucide-react';
import Footer from '../components/Footer';
import '../styles/advertorial.css';

const SevenReasons = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 21,
    minutes: 9,
    seconds: 31
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prevTime => {
        let { days, hours, minutes, seconds } = prevTime;
        
        if (seconds > 0) {
          seconds--;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes--;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours--;
            } else {
              hours = 23;
              if (days > 0) {
                days--;
              }
            }
          }
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => String(num).padStart(2, '0');

  return (
    <div className="advertorial-page eye-mask-theme">
      <Helmet>
        <title>7 Reasons Why This Steam Eye Mask Is Becoming a Must-Have for Dry Eyes & Eye Strain Relief in 2025</title>
        <meta 
          name="description" 
          content="Kickin'On Steam Eye Mask a self-heating, disposable, hygienic eye mask designed to reduce eye strain, soothe dryness deeply relax tired eyes in 10–15 minutes." 
        />
        <meta 
          name="keywords" 
          content="dry eyes, eye strain, eye strain relief, steam eye mask, microwave eye mask, tired eyes, energy, fatigue, lubricating eye" 
        />
        <meta property="og:title" content="7 Reasons Why This Steam Eye Mask Is Becoming a Must-Have for Dry Eyes & Eye Strain Relief" />
        <meta 
          property="og:description" 
          content="Kickin'On Steam Eye Mask a self-heating, disposable, hygienic eye mask designed to reduce eye strain, soothe dryness deeply relax tired eyes in 10–15 minutes." 
        />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="7 Reasons Why This Steam Eye Mask Is Becoming a Must-Have for Dry Eyes & Eye Strain Relief" />
        <meta 
          name="twitter:description" 
          content="Kickin'On Steam Eye Mask a self-heating, disposable, hygienic eye mask designed to reduce eye strain, soothe dryness deeply relax tired eyes in 10–15 minutes." 
        />
        <link rel="canonical" href="https://yourdomain.com/7-reasons" />
      </Helmet>
      
      {/* Top Banner with Timer */}
      <div className="top-banner">
        <div className="banner-content">
          <span className="sale-text">SALE ENDS SOON</span>
          <div className="countdown-timer">
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.hours)}</span>
              <span className="time-label">HRS</span>
            </div>
            <span className="separator">:</span>
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.minutes)}</span>
              <span className="time-label">MIN</span>
            </div>
            <span className="separator">:</span>
            <div className="time-unit">
              <span className="time-value">{formatTime(timeLeft.seconds)}</span>
              <span className="time-label">SEC</span>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-container">
        <div className="progress-bar"></div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Warning Banner */}
        <div className="warning-banner">
          <AlertTriangle className="warning-icon" />
          <span>Warning: Microwave Heated Eye Masks</span>
        </div>

        {/* Hero Section */}
        <section className="hero-section">
          <h1 className="hero-title">
            7 Reasons Why This Steam Eye Mask Is Becoming a Must-Have for <br />
            <span className="hero-highlight">Dry Eyes & Eye Strain Relief in 2025</span>
          </h1>
          
          <div className="author-info">
            <p className="author-name">By Jessica M.</p>
            <p className="publish-date">Last Updated Dec 3, 2025</p>
          </div>

          <div className="summary-box">
            <h3 className="summary-title">Summary:</h3>
            <p className="summary-text">
              Introducing the <strong>Kickin'On Steam Eye Mask</strong> — a self-heating, disposable, 
              hygienic eye mask designed to reduce eye strain, soothe dryness, and deeply relax tired 
              eyes in just 10–15 minutes.
            </p>
            <p className="summary-text">
              Powered by <strong>HydroTherm Reset™</strong>, this breakthrough warm-steam technology 
              supports natural tear moisture, melts tension around the eyes, and works anywhere — 
              no microwave, no cords, no fuss.
            </p>
          </div>

          <p className="intro-text">
            Here are the 7 reasons this simple eye mask is gaining massive popularity in 2025…
          </p>
        </section>

        {/* Reason 1 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason1.jpg" 
              alt="Woman relaxing with Kickin'On steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">1. Instant Warmth — No Microwave Required</h2>
            <p className="feature-description">
              Warm compresses work, but traditional microwave masks come with a catch:
            </p>
            <p className="feature-description highlight-text">
              Scientists found 747 types of bacteria living inside household microwaves… and most 
              people are unknowingly pressing that warmed bacteria onto the thinnest skin on their 
              body: their eyes (making dry eyes or strained eyes worse)...
            </p>
            <p className="feature-description">
              Kickin'On's Steam Eye Mask activates the moment it's opened — delivering soothing warm 
              steam without microwaves, batteries, plugs, or preparation.
            </p>
            <p className="feature-description">
              Perfect for bed, flights, the office, or anytime your eyes feel fatigued.
            </p>
          </div>
        </section>

        {/* Reason 2 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason2.jpg" 
              alt="HydroTherm Reset technology for eye strain relief" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">2. HydroTherm Reset™ Helps Reduce Eye Strain</h2>
            <p className="feature-description">
              Eye strain often comes from tight muscles around the eyes and poor tear evaporation.
            </p>
            <p className="feature-description">
              HydroTherm Reset™ uses gentle steam + consistent warmth to:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Relax overworked eye muscles</li>
              <li><Check className="check-icon" /> Soften blocked oils in the eyelid glands</li>
              <li><Check className="check-icon" /> Support natural moisture</li>
              <li><Check className="check-icon" /> Reduce that "tight, tired" feeling</li>
            </ul>
            <p className="feature-description">
              Most people feel relief in the first few minutes.
            </p>
          </div>
        </section>

        {/* Reason 3 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason3.jpg" 
              alt="Deep hydration with Kickin'On steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">3. Deep Hydration for Dry, Gritty Eyes</h2>
            <p className="feature-description">
              Dry eyes often stem from inadequate tear quality, not just tear quantity.
            </p>
            <p className="feature-description">
              Warm steam helps:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Improve tear film stability</li>
              <li><Check className="check-icon" /> Soften meibomian oils (the natural moisturizers of the eye)</li>
              <li><Check className="check-icon" /> Reduce dryness, grittiness, and "sandpaper eyes"</li>
            </ul>
            <p className="feature-quote">
              "My eyes finally feel soft again."
            </p>
          </div>
        </section>

        {/* Reason 4 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason4.jpg" 
              alt="Hygienic disposable steam eye masks" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">4. Hygienic & Disposable — A Fresh Mask Every Time</h2>
            <p className="feature-description">
              Reusable microwave masks absorb oils, makeup, bacteria, and dust.
            </p>
            <p className="feature-description">
              But Kickin'On masks are:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Individually sealed</li>
              <li><Check className="check-icon" /> Single-use</li>
              <li><Check className="check-icon" /> Clean and hygienic every time</li>
            </ul>
            <p className="feature-description">
              No odors. No crust. No questionable bacteria near your eyes.
            </p>
          </div>
        </section>

        {/* CTA Section 1 */}
        <section className="inline-cta-section">
          <div className="cta-two-column">
            <div className="cta-image-column">
              <img 
                src="/images/offer-section.jpg" 
                alt="Kickin'On Steam Eye Mask - Buy More Save More" 
                className="cta-offer-image"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="cta-content-column">
              <div className="inline-cta-banner">SALE ENDS SOON</div>
              <div className="inline-cta-timer">
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.hours)}</span>
                  <span className="inline-time-label">hrs</span>
                </div>
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.minutes)}</span>
                  <span className="inline-time-label">min</span>
                </div>
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.seconds)}</span>
                  <span className="inline-time-label">sec</span>
                </div>
              </div>
              <h3 className="inline-cta-title">Kickin'On Steam Eye Mask</h3>
              <p className="inline-cta-subtitle">
                Dry Eyes & Eye Strain Relief in 10-30 Minutes - Your Eyes Are Worth It
              </p>
              <a 
                href="https://kickinon.com/products/kickinon-steam-eye-mask" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-cta-button"
              >
                GET 45% OFF
              </a>
              <div className="inline-cta-info">
                <span className="risk-badge">Sell-out Risk: <span className="high">High</span></span>
                <span className="separator">|</span>
                <span className="free-shipping"><strong>FREE</strong> shipping for 3 packs</span>
              </div>
              <p className="inline-guarantee">Try it today with a 30-Day Money Back Guarantee!</p>
            </div>
          </div>
        </section>

        {/* Reason 5 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason5.jpg" 
              alt="Kickin'On steam eye mask perfect for travel and flights" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">5. Incredible for Travel — Especially Planes</h2>
            <p className="feature-description">
              Airplane cabins are drier than most deserts (often &lt;15% humidity).
            </p>
            <p className="feature-description">
              That dryness makes eye strain, fatigue, and irritation much worse.
            </p>
            <p className="feature-description">
              Kickin'On masks are:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Slim enough for carry-on</li>
              <li><Check className="check-icon" /> TSA-friendly</li>
              <li><Check className="check-icon" /> Perfect for red-eyes, long flights & layovers</li>
            </ul>
            <p className="feature-quote">
              Travelers call it their "30,000-ft eye reset."
            </p>
            <p className="feature-description">
              Eyes deserve first class treatment...
            </p>
          </div>
        </section>

        {/* Reason 6 */}
        <section className="feature-section reverse">
          <div className="feature-image">
            <img 
              src="/images/reason6.jpg" 
              alt="Relaxation ritual with Kickin'On steam eye mask" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">6. A Relaxation Ritual You Actually Look Forward To</h2>
            <p className="feature-description">
              Whether at bedtime or after a long workday, this mask creates a 10–15 minute moment of calm.
            </p>
            <p className="feature-description">
              Users say it helps them:
            </p>
            <ul className="feature-list">
              <li><Check className="check-icon" /> Unwind</li>
              <li><Check className="check-icon" /> Release face tension</li>
              <li><Check className="check-icon" /> Prepare for deeper sleep</li>
              <li><Check className="check-icon" /> Detach from stress</li>
            </ul>
            <p className="feature-description">
              It's the smallest ritual with the biggest payoff.
            </p>
          </div>
        </section>

        {/* Reason 7 */}
        <section className="feature-section">
          <div className="feature-image">
            <img 
              src="/images/reason7.jpg" 
              alt="Convenient and comfortable steam eye mask design" 
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="feature-content">
            <h2 className="feature-title">7. Designed for Convenience, Comfort & Consistency</h2>
            <p className="feature-description">
              No microwaves. <br />
              No cords. <br />
              No charging.
            </p>
            <p className="feature-description">
              No figuring out whether it's too hot or not hot enough.
            </p>
            <p className="feature-description">
              Just open → place → feel your eyes melt into relaxation.
            </p>
            <p className="feature-description">
              A true "set it and forget it" eye treatment.
            </p>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="inline-cta-section final-cta">
          <div className="cta-two-column">
            <div className="cta-image-column">
              <img 
                src="/images/offer-section.jpg" 
                alt="Kickin'On Steam Eye Mask - Buy More Save More" 
                className="cta-offer-image"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="cta-content-column">
              <div className="inline-cta-banner">SALE ENDS SOON</div>
              <div className="inline-cta-timer">
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.hours)}</span>
                  <span className="inline-time-label">hrs</span>
                </div>
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.minutes)}</span>
                  <span className="inline-time-label">min</span>
                </div>
                <div className="inline-time-unit">
                  <span className="inline-time-value">{formatTime(timeLeft.seconds)}</span>
                  <span className="inline-time-label">sec</span>
                </div>
              </div>
              <h3 className="inline-cta-title">Kickin'On Steam Eye Mask</h3>
              <p className="inline-cta-subtitle">
                Dry Eyes & Eye Strain Relief in 10-30 Minutes - Your Eyes Are Worth It
              </p>
              <a 
                href="https://kickinon.com/products/kickinon-steam-eye-mask" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-cta-button"
              >
                GET 45% OFF
              </a>
              <div className="inline-cta-info">
                <span className="risk-badge">Sell-out Risk: <span className="high">High</span></span>
                <span className="separator">|</span>
                <span className="free-shipping"><strong>FREE</strong> shipping for 3 packs</span>
              </div>
              <p className="inline-guarantee">Try it today with a 30-Day Money Back Guarantee!</p>
            </div>
          </div>
        </section>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SevenReasons;